import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Activity, Zap, AlertTriangle, CheckCircle, XCircle, 
  RefreshCw, Clock, TrendingUp, Server, Loader2, ArrowLeft
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIMonitoringDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [metrics, setMetrics] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const token = localStorage.getItem('adminToken');
  
  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchData = async () => {
    try {
      const [metricsRes, healthRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-ads/metrics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/ai-ads/health`)
      ]);
      
      setMetrics(metricsRes.data);
      setHealth(healthRes.data);
    } catch (error) {
      console.error('Error fetching AI metrics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };
  
  const handleResetProvider = async (provider) => {
    try {
      await axios.post(
        `${API_URL}/api/ai-ads/metrics/reset/${provider}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Provider Reset',
        description: `${provider} has been reset and is now available`
      });
      
      fetchData();
    } catch (error) {
      console.error('Error resetting provider:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset provider',
        variant: 'destructive'
      });
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-500';
      case 'unavailable': return 'bg-red-500';
      case 'degraded': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Available</Badge>;
      case 'unavailable':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> Unavailable</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={() => navigate('/admin')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Activity className="h-6 w-6" />
                  AI Service Monitoring
                </h1>
                <p className="text-indigo-100">Real-time AI provider status and metrics</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className={`border-l-4 ${health?.status === 'healthy' ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">System Status</p>
                  <p className="text-2xl font-bold capitalize">{health?.status || 'Unknown'}</p>
                </div>
                <div className={`p-3 rounded-full ${health?.status === 'healthy' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  {health?.status === 'healthy' ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-6 w-6 text-yellow-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Available Providers</p>
                  <p className="text-2xl font-bold">
                    {health?.available_providers} / {health?.total_providers}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <Server className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Last Updated</p>
                  <p className="text-lg font-medium">
                    {metrics?.timestamp ? new Date(metrics.timestamp).toLocaleTimeString() : 'N/A'}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Provider Cards */}
        <h2 className="text-xl font-semibold mb-4">AI Providers</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {metrics?.providers?.map((provider) => (
            <Card key={provider.provider} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(provider.status)}`}></div>
                    <CardTitle className="text-lg">{provider.name}</CardTitle>
                  </div>
                  {getStatusBadge(provider.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Success Rate */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Success Rate</span>
                    <span className="font-medium">{provider.success_rate}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        provider.success_rate >= 90 ? 'bg-green-500' :
                        provider.success_rate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${provider.success_rate}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500">Requests Today</p>
                    <p className="text-xl font-bold">{provider.requests_today}</p>
                    {provider.daily_limit && (
                      <p className="text-xs text-gray-400">
                        Limit: {provider.daily_limit.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500">Avg Latency</p>
                    <p className="text-xl font-bold">{provider.avg_latency_ms}ms</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-green-600">Successes</p>
                    <p className="text-xl font-bold text-green-700">{provider.successes}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-red-600">Failures</p>
                    <p className="text-xl font-bold text-red-700">{provider.failures}</p>
                  </div>
                </div>
                
                {/* Last Error */}
                {provider.last_error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs text-red-600 font-medium">Last Error:</p>
                    <p className="text-xs text-red-700 truncate">{provider.last_error}</p>
                  </div>
                )}
                
                {/* Last Success */}
                {provider.last_success && (
                  <div className="text-xs text-gray-500">
                    Last success: {new Date(provider.last_success).toLocaleString()}
                  </div>
                )}
                
                {/* Reset Button */}
                {provider.status === 'unavailable' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleResetProvider(provider.provider)}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reset Provider
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Fallback Order Info */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              Fallback Order
            </CardTitle>
            <CardDescription>
              When a provider fails, the system automatically tries the next one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              {metrics?.providers?.sort((a, b) => {
                const order = { gemini: 1, groq: 2, together: 3 };
                return (order[a.provider] || 99) - (order[b.provider] || 99);
              }).map((provider, index) => (
                <React.Fragment key={provider.provider}>
                  <div className={`flex-shrink-0 p-4 rounded-lg border-2 ${
                    provider.status === 'available' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-red-300 bg-red-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{index + 1}.</span>
                      <span className="font-semibold">{provider.name}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {provider.status === 'available' ? '✓ Active' : '✗ Skipped'}
                    </div>
                  </div>
                  {index < metrics.providers.length - 1 && (
                    <div className="text-gray-400 text-2xl flex-shrink-0">→</div>
                  )}
                </React.Fragment>
              ))}
              <div className="flex-shrink-0 p-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{(metrics?.providers?.length || 0) + 1}.</span>
                  <span className="font-semibold text-gray-600">Rule-Based</span>
                </div>
                <div className="text-xs text-gray-500">Emergency fallback</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIMonitoringDashboard;
